/**
 * RoleGuard — route-level access control for role-gated resources.
 *
 * Renders children only when the current user has one of the `allow` roles.
 * Otherwise redirects to `fallback`; by default — to the role's home page
 * (`/` for admins, `/homework` for everyone else), so an admin who lands on an
 * unknown route is not dumped into the mentor's homework queue.
 *
 * Usage:
 *   <RoleGuard allow={["admin"]}>
 *     <AdminOnlyPage />
 *   </RoleGuard>
 *
 * Три состояния роли (loading / известная роль / отказ) разбирает общий
 * `useAppRole` — та же семантика, что у `MentorSider`. См. hooks/use-app-role.
 */

import { Spin } from "antd";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useAppRole } from "../hooks/use-app-role";
import { type AppRole, homePathFor } from "../lib/panel-access";

interface RoleGuardProps {
  /** Roles that are allowed to see this content. */
  allow: AppRole[];
  /** Where to redirect unauthorized users. Defaults to the role's home page. */
  fallback?: string;
  children: ReactNode;
}

export function RoleGuard({ allow, fallback, children }: RoleGuardProps): JSX.Element {
  const role = useAppRole();

  if (role === "loading") {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (role === "denied" || !allow.includes(role)) {
    return <Navigate to={fallback ?? homePathFor(role === "denied" ? null : role)} replace />;
  }

  return <>{children}</>;
}
